// @flow

import React from "react";
import classNames from "classnames";

import options from "../utils/options";
import ComboBox from "../../combobox";
import type { SchemaTableWidgetProps } from "../types";

import styles from "./hours-and-minutes-select.scss";

const SECONDS_IN_MINUTE = 60;
const SECONDS_IN_HOUR = 3600;

export type HoursAndMinutesSelectWidgetState = $Exact<{
  touched: boolean,
}>;

export class HoursAndMinutesSelectWidget extends React.Component<
  SchemaTableWidgetProps<number>,
  HoursAndMinutesSelectWidgetState,
> {
  state = {
    touched: false,
  };

  componentDidUpdate(prevProps: SchemaTableWidgetProps<number>) {
    // this will only happen as part of a request to hide errors
    if (!this.props.touched && prevProps.touched) {
      this.setState({ touched: false });
    }
  }

  options = (limit: number): Array<{ label: string, value: number }> =>
    [...new Array(limit)].map((_: *, i: number) => ({
      label: `${i.toString().padStart(2, "0")}`,
      value: i,
    }));

  get hours() {
    return Math.floor(this.props.data / SECONDS_IN_HOUR);
  }

  get minutes() {
    return Math.floor(this.props.data / SECONDS_IN_MINUTE) - this.hours * SECONDS_IN_MINUTE;
  }

  get values() {
    return [this.hours, this.minutes];
  }

  get displayValue() {
    return `${this.hours}h ${this.minutes}m`;
  }

  hasUiSchemaError = (data: number): boolean => {
    if (!this.state.touched) {
      return false;
    }

    return data === null || data === undefined || typeof data !== "number";
  };

  hasError = (value: number): boolean => {
    return !!this.props.error || this.hasUiSchemaError(value);
  };

  updateHours = (option: number): void => {
    this.setState({ touched: true });
    this.props.onChange(option * SECONDS_IN_HOUR + this.minutes * SECONDS_IN_MINUTE);
  };

  updateMinutes = (option: number): void => {
    this.setState({ touched: true });
    this.props.onChange(this.hours * SECONDS_IN_HOUR + option * SECONDS_IN_MINUTE);
  };

  render() {
    const disabled = options.resolveBoolean((this.props.options || {}).disabled, this.props.row);

    if (!this.props.active) {
      return (
        <div
          className={classNames("hours-and-minutes-select__readonly", {
            text__disabled: disabled,
          })}
        >
          {this.displayValue}
        </div>
      );
    }

    return (
      <div
        className={classNames("hours-and-minutes-select__container", {
          "hours-and-minutes-select__container--disabled": disabled,
          "hours-and-minutes-select__container--error": this.hasError(this.props.data),
        })}
      >
        <ComboBox
          className="w-100"
          options={this.options(24)}
          value={this.values[0]}
          onChange={this.updateHours}
          disabled={disabled}
        />
        <ComboBox
          className="w-100"
          options={this.options(60)}
          value={this.values[1]}
          onChange={this.updateMinutes}
          disabled={disabled}
        />
      </div>
    );
  }
}

export default HoursAndMinutesSelectWidget;
